import { Inbox } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface EmptyProps extends React.HTMLAttributes<HTMLDivElement> {
	image?: React.ReactNode | "simple" | "default";
	imageStyle?: React.CSSProperties;
	description?: React.ReactNode;
}

const Empty = forwardRef<HTMLDivElement, EmptyProps>(
	(
		{ className, image = "default", imageStyle, description = "暂无数据", children, ...props },
		ref,
	) => {
		const renderImage = () => {
			if (image === "simple") {
				return <Inbox className="h-10 w-10 text-gray-300 dark:text-gray-600" style={imageStyle} />;
			}

			if (image === "default") {
				return (
					<div className="flex flex-col items-center justify-center" style={imageStyle}>
						<svg
							className="h-16 w-16 text-gray-200 dark:text-gray-700"
							viewBox="0 0 64 41"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<g transform="translate(0 1)" fill="none" fillRule="evenodd">
								<ellipse
									className="fill-gray-100 dark:fill-gray-800"
									cx="32"
									cy="33"
									rx="32"
									ry="7"
								/>
								<g className="stroke-gray-300 dark:stroke-gray-600" strokeWidth="2">
									<path d="M55 12.76L44.854 1.258C44.367.474 43.656 0 42.907 0H21.093c-.749 0-1.46.474-1.947 1.257L9 12.761V22h46v-9.24z" />
									<path
										d="M41.613 15.931c0-1.605.994-2.93 2.227-2.931H55v18.137C55 33.26 53.68 35 52.05 35h-40.1C10.32 35 9 33.259 9 31.137V13h11.16c1.233 0 2.227 1.323 2.227 2.928v.022c0 1.605 1.005 2.901 2.237 2.901h14.752c1.232 0 2.237-1.308 2.237-2.913v-.007z"
										className="fill-white dark:fill-gray-800"
									/>
								</g>
							</g>
						</svg>
					</div>
				);
			}

			return image;
		};

		return (
			<div
				ref={ref}
				className={cn("flex flex-col items-center justify-center py-8", className)}
				{...props}
			>
				<div className="mb-4">{renderImage()}</div>
				{description && <p className="text-sm text-gray-400 dark:text-gray-500">{description}</p>}
				{children && <div className="mt-4">{children}</div>}
			</div>
		);
	},
);

Empty.displayName = "Empty";

// Preset configurations
interface EmptyComponent extends React.ForwardRefExoticComponent<
	EmptyProps & React.RefAttributes<HTMLDivElement>
> {
	PRESENTED_IMAGE_SIMPLE: string;
	PRESENTED_IMAGE_DEFAULT: string;
}

const EmptyWithPresets = Empty as EmptyComponent;
EmptyWithPresets.PRESENTED_IMAGE_SIMPLE = "simple";
EmptyWithPresets.PRESENTED_IMAGE_DEFAULT = "default";

export { EmptyWithPresets as Empty };
